// src/services/api.js - COMPLETE FIXED VERSION WITH BLOG FIX

import config, { getApiUrl, getBackendUrl, getMediaUrl, log } from '../utils/environment.js';

// Enhanced API request with CSRF token handling
const apiRequest = async (endpoint, options = {}) => {
  const url = endpoint.startsWith('http') ? endpoint : getApiUrl(endpoint);
  
  log.info(`📡 API Request: ${options.method || 'GET'} ${url}`);

  // Get CSRF token from cookie or fetch it
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
  };
  
  let csrfToken = getCookie('csrftoken') || getCookie('csrfToken');
  
  // If no CSRF token and this is a POST/PUT/PATCH/DELETE request, fetch it first
  if (!csrfToken && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(options.method || 'GET')) {
    try {
      const csrfResponse = await fetch(getApiUrl('/csrf/'), {
        method: 'GET',
        credentials: 'include',
      });
      
      if (csrfResponse.ok) {
        const csrfData = await csrfResponse.json();
        csrfToken = csrfData.csrfToken;
        log.info('CSRF token fetched successfully:', csrfToken);
      }
    } catch (error) {
      log.warn('Could not fetch CSRF token:', error);
    }
  }

  const defaultHeaders = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...(csrfToken && { 'X-CSRFToken': csrfToken }),
    // Add referer header
    'Referer': window.location.origin,
  };

  const configOptions = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
    credentials: 'include',  // Important for cookies
    mode: 'cors',  // Explicitly set CORS mode
  };

  try {
    const startTime = Date.now();
    const response = await fetch(url, configOptions);
    const duration = Date.now() - startTime;
    
    log.info(`📥 API Response: ${response.status} ${response.statusText} (${duration}ms)`);
    
    if (response.status === 404) {
      log.warn(`Endpoint not found: ${endpoint}`);
      return null;
    }
    
    if (!response.ok) {
      let errorData = {};
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = { detail: await response.text() };
      }
      
      throw {
        status: response.status,
        message: errorData.detail || errorData.message || errorData.error || `API Error: ${response.statusText}`,
        data: errorData,
      };
    }

    if (response.status === 204) {
      return null;
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const data = await response.json();
      log.debug('API Response Data:', data);
      return data;
    }
    
    return await response.text();
    
  } catch (error) {
    log.error(`❌ API Error (${endpoint}):`, error);
    
    if (error.status) {
      throw error;
    }
    
    throw {
      status: 0,
      message: error.message || 'Network error',
      data: null,
    };
  }
};

// Cache implementation
class ApiCache {
  constructor(maxSize = 100) {
    this.cache = new Map();
    this.maxSize = maxSize;
  }

  get(key) {
    const item = this.cache.get(key);
    if (!item) return null;
    
    if (Date.now() - item.timestamp > 300000) { // 5 minutes
      this.cache.delete(key);
      return null;
    }
    
    return item.data;
  }

  set(key, data) {
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
    
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  delete(key) {
    this.cache.delete(key);
  }

  clear() {
    this.cache.clear();
  }
}

const apiCache = new ApiCache();

// Newsletter API - FIXED
export const newsletterApi = {
  subscribe: async (email) => {
    try {
      // First, try to get CSRF token
      try {
        const csrfResponse = await fetch(getApiUrl('/csrf/'), {
          method: 'GET',
          credentials: 'include',
        });
        
        if (csrfResponse.ok) {
          const csrfData = await csrfResponse.json();
          log.info('CSRF token received:', csrfData.csrfToken);
        }
      } catch (csrfError) {
        log.warn('Could not fetch CSRF token:', csrfError);
      }
      
      const response = await apiRequest('/newsletter/subscribers/', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      
      return response;
    } catch (error) {
      log.error('Newsletter subscription error:', error);
      throw error;
    }
  },
  
  unsubscribe: async (email) => {
    try {
      const response = await apiRequest('/newsletter/subscribers/unsubscribe/', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      
      return response;
    } catch (error) {
      log.error('Newsletter unsubscribe error:', error);
      throw error;
    }
  },
};

// Blog API - FIXED
export const blogApi = {
  getArticles: async (params = {}) => {
    const cacheKey = `articles_${JSON.stringify(params)}`;
    const cached = apiCache.get(cacheKey);
    if (cached) {
      log.debug('Returning cached articles');
      return cached;
    }
    
    try {
      const queryParams = new URLSearchParams(params).toString();
      const endpoint = `/blog/articles/${queryParams ? `?${queryParams}` : ''}`;
      
      log.info('Fetching articles from:', endpoint);
      const data = await apiRequest(endpoint);
      
      log.info('Raw articles response:', data);
      
      // Handle different response formats
      let processed = [];
      
      if (Array.isArray(data)) {
        processed = data;
      } else if (data && typeof data === 'object') {
        if (Array.isArray(data.results)) {
          processed = data.results;
        } else if (Array.isArray(data.data)) {
          processed = data.data;
        } else if (data.count !== undefined && Array.isArray(data.results)) {
          // Django REST pagination format
          processed = data.results;
        } else {
          // Single object, wrap in array
          processed = [data];
        }
      }
      
      log.info(`✅ Processed ${processed.length} articles`);
      apiCache.set(cacheKey, processed);
      return processed;
    } catch (error) {
      log.error('Error fetching articles:', error);
      return [];
    }
  },
  
  getArticleBySlug: async (slug) => {
    const cacheKey = `article_${slug}`;
    const cached = apiCache.get(cacheKey);
    if (cached) return cached;
    
    try {
      log.info('Fetching article:', slug);
      const data = await apiRequest(`/blog/articles/${slug}/`);
      
      if (data) {
        apiCache.set(cacheKey, data);
      }
      
      return data;
    } catch (error) {
      log.error(`Error fetching article ${slug}:`, error);
      return null;
    }
  },
  
  getFeaturedArticles: async () => {
    const cacheKey = 'featured_articles';
    const cached = apiCache.get(cacheKey);
    if (cached) return cached;
    
    try {
      // Try multiple endpoints
      const endpoints = [
        '/blog/articles/featured/',
        '/blog/articles/?is_featured=true',
        '/blog/articles/?featured=true'
      ];
      
      for (const endpoint of endpoints) {
        try {
          log.info('Trying featured endpoint:', endpoint);
          const data = await apiRequest(endpoint);
          
          if (data) {
            let processed = [];
            
            if (Array.isArray(data)) {
              processed = data;
            } else if (data && typeof data === 'object') {
              if (Array.isArray(data.results)) {
                processed = data.results;
              } else if (Array.isArray(data.data)) {
                processed = data.data;
              }
            }
            
            if (processed.length > 0) {
              log.info(`✅ Found ${processed.length} featured articles from ${endpoint}`);
              apiCache.set(cacheKey, processed);
              return processed;
            }
          }
        } catch (error) {
          log.warn(`Failed to fetch from ${endpoint}:`, error);
          continue;
        }
      }
      
      // Fallback: get first few articles
      log.warn('No featured articles found, using fallback');
      const allArticles = await blogApi.getArticles({ limit: 3 });
      apiCache.set(cacheKey, allArticles);
      return allArticles;
      
    } catch (error) {
      log.error('Error fetching featured articles:', error);
      return [];
    }
  },
  
  getTrendingArticles: async () => {
    const cacheKey = 'trending_articles';
    const cached = apiCache.get(cacheKey);
    if (cached) return cached;
    
    try {
      const endpoints = [
        '/blog/articles/trending/',
        '/blog/articles/?is_trending=true',
        '/blog/articles/?trending=true',
        '/blog/articles/?ordering=-views&limit=6'
      ];
      
      for (const endpoint of endpoints) {
        try {
          log.info('Trying trending endpoint:', endpoint);
          const data = await apiRequest(endpoint);
          
          if (data) {
            let processed = [];
            
            if (Array.isArray(data)) {
              processed = data;
            } else if (data && typeof data === 'object') {
              if (Array.isArray(data.results)) {
                processed = data.results;
              } else if (Array.isArray(data.data)) {
                processed = data.data;
              }
            }
            
            if (processed.length > 0) {
              log.info(`✅ Found ${processed.length} trending articles`);
              apiCache.set(cacheKey, processed);
              return processed;
            }
          }
        } catch (error) {
          continue;
        }
      }
      
      const fallback = await blogApi.getArticles({ limit: 6, ordering: '-views' });
      apiCache.set(cacheKey, fallback);
      return fallback;
      
    } catch (error) {
      log.error('Error fetching trending articles:', error);
      return [];
    }
  },
  
  getLatestArticles: async (limit = 5) => {
    const cacheKey = `latest_articles_${limit}`;
    const cached = apiCache.get(cacheKey);
    if (cached) return cached;
    
    try {
      const endpoints = [
        `/blog/articles/latest/`,
        `/blog/articles/?ordering=-publish_date&limit=${limit}`,
        '/blog/articles/'
      ];
      
      for (const endpoint of endpoints) {
        try {
          const data = await apiRequest(endpoint);
          
          if (data) {
            let processed = [];
            
            if (Array.isArray(data)) {
              processed = data;
            } else if (data && typeof data === 'object') {
              if (Array.isArray(data.results)) {
                processed = data.results;
              } else if (Array.isArray(data.data)) {
                processed = data.data;
              }
            }
            
            if (processed.length > 0) {
              // Sort by publish_date (newest first)
              processed.sort((a, b) => {
                return new Date(b.publish_date) - new Date(a.publish_date);
              });
              
              // Take only the specified limit
              processed = processed.slice(0, limit);
              
              apiCache.set(cacheKey, processed);
              return processed;
            }
          }
        } catch (error) {
          continue;
        }
      }
      
      const fallback = await blogApi.getArticles({ limit: limit });
      apiCache.set(cacheKey, fallback);
      return fallback;
      
    } catch (error) {
      log.error('Error fetching latest articles:', error);
      return [];
    }
  },
  
  getCategories: async () => {
    const cacheKey = 'blog_categories';
    const cached = apiCache.get(cacheKey);
    if (cached) return cached;
    
    try {
      log.info('Fetching blog categories');
      const data = await apiRequest('/blog/categories/');
      
      let processed = [];
      
      if (Array.isArray(data)) {
        processed = data;
      } else if (data && typeof data === 'object') {
        if (Array.isArray(data.results)) {
          processed = data.results;
        } else if (Array.isArray(data.data)) {
          processed = data.data;
        }
      }
      
      log.info(`✅ Found ${processed.length} categories`);
      apiCache.set(cacheKey, processed);
      return processed;
    } catch (error) {
      log.error('Error fetching categories:', error);
      return [];
    }
  },
  
  searchArticles: async (query) => {
    try {
      const data = await apiRequest(`/blog/articles/search/?q=${encodeURIComponent(query)}`);
      
      if (Array.isArray(data)) {
        return data;
      } else if (data && typeof data === 'object' && Array.isArray(data.results)) {
        return data.results;
      }
      
      return [];
    } catch (error) {
      log.error('Error searching articles:', error);
      return [];
    }
  }
};

// Template API
export const templateApi = {
  getTemplates: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const endpoint = `/templates/${queryParams ? `?${queryParams}` : ''}`;
      const data = await apiRequest(endpoint);
      return Array.isArray(data) ? data : (data?.results || data?.data || []);
    } catch (error) {
      log.error('Error fetching templates:', error);
      return [];
    }
  },
  
  getTemplateBySlug: async (slug) => {
    try {
      const data = await apiRequest(`/templates/${slug}/`);
      return data;
    } catch (error) {
      log.error(`Error fetching template ${slug}:`, error);
      return null;
    }
  },
  
  getTemplateCategories: async () => {
    try {
      const data = await apiRequest('/template-categories/');
      return Array.isArray(data) ? data : (data?.results || data?.data || []);
    } catch (error) {
      log.error('Error fetching template categories:', error);
      return [];
    }
  },
  
  incrementDownload: async (slug) => {
    try {
      return await apiRequest(`/templates/${slug}/increment_download/`, {
        method: 'POST'
      });
    } catch (error) {
      log.error(`Error incrementing download for ${slug}:`, error);
      return null;
    }
  }
};

// Page API
export const pageApi = {
  getHomePage: async () => {
    try {
      const data = await apiRequest('/content/home/');
      log.info('Home page data:', data);
      return data;
    } catch (error) {
      log.error('Error fetching home page:', error);
      return null;
    }
  },
  
  getAboutPage: async () => {
    try {
      return await apiRequest('/content/about/');
    } catch (error) {
      log.error('Error fetching about page:', error);
      return null;
    }
  },
  
  getContactPage: async () => {
    try {
      return await apiRequest('/content/contact/');
    } catch (error) {
      log.error('Error fetching contact page:', error);
      return null;
    }
  },
  
  getProfessionPage: async () => {
    try {
      return await apiRequest('/content/profession/');
    } catch (error) {
      log.error('Error fetching profession page:', error);
      return null;
    }
  },
};

// Profession Categories API


// Site Configuration API
export const siteApi = {
  getConfig: async () => {
    try {
      return await apiRequest('/site/config/');
    } catch (error) {
      log.error('Error fetching site config:', error);
      return null;
    }
  },
};

// SEO Service
export const seoService = {
  generateMetaTags: (seoData, defaultTitle = 'Resumeslip', defaultDescription = 'Build professional resumes and slips') => {
    const title = seoData?.meta_title || defaultTitle;
    const description = seoData?.meta_description || defaultDescription;
    const keywords = seoData?.meta_keywords || 'resume, cv, slip, professional, builder, free';
    const canonical = seoData?.canonical_url || window.location.href;
    const robots = seoData?.robots_txt || 'index, follow';

    return {
      title,
      meta: [
        { name: 'description', content: description },
        { name: 'keywords', content: keywords },
        { name: 'robots', content: robots },
        { property: 'og:title', content: seoData?.og_title || title },
        { property: 'og:description', content: seoData?.og_description || description },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: canonical },
        ...(seoData?.og_image ? [{ property: 'og:image', content: seoData.og_image }] : []),
        { name: 'twitter:title', content: seoData?.twitter_title || title },
        { name: 'twitter:description', content: seoData?.twitter_description || description },
        { name: 'twitter:card', content: 'summary_large_image' },
        ...(seoData?.twitter_image ? [{ name: 'twitter:image', content: seoData.twitter_image }] : []),
        { rel: 'canonical', href: canonical },
      ],
    };
  },
};

// API hook
export const useApi = () => {
  const fetchWithCache = async (key, fetchFn, forceRefresh = false) => {
    if (!forceRefresh) {
      const cached = apiCache.get(key);
      if (cached) return cached;
    }
    
    try {
      const data = await fetchFn();
      apiCache.set(key, data);
      return data;
    } catch (error) {
      if (!forceRefresh) {
        const cached = apiCache.get(key);
        if (cached) return cached;
      }
      throw error;
    }
  };
  
  const invalidateCache = (key) => {
    if (key) {
      apiCache.delete(key);
    } else {
      apiCache.clear();
    }
  };
  
  return { fetchWithCache, invalidateCache };
};

// Health check
export const apiHealthCheck = async () => {
  try {
    const response = await fetch(getApiUrl('/'), {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
    });
    return response.ok;
  } catch (error) {
    log.warn('API Health Check failed:', error);
    return false;
  }
};

// Clear all caches
export const clearAllCaches = () => {
  apiCache.clear();
  log.info('All API caches cleared');
};
export const privacyApi = {
  getPrivacyPolicy: async () => {
    try {
      return await apiRequest('/privacy-policy/');
    } catch (error) {
      log.error('Error fetching privacy policy:', error);
      return null;
    }
  },
};

export const templatePageApi = {
  getTemplatePage: async () => {
    try {
      const data = await apiRequest('/template-page/');
      return data;
    } catch (error) {
      log.error('Error fetching template page:', error);
      return null;
    }
  },
};

export default {
  pageApi,
  blogApi,
  templateApi,
  newsletterApi,
  siteApi,
  templatePageApi,  
  seoService,
  useApi,
  apiHealthCheck,
  clearAllCaches,
  privacyApi,
};